import type { BuiltInTool, JsonSchema } from './types'

const TARGET_ITEM: JsonSchema = {
    type: 'object',
    properties: {
        id: { type: 'string' },
        name: { type: 'string' },
        unit: { type: 'string' },
        targetValue: { type: 'number' },
        targetDirection: { type: 'string', enum: ['at_least', 'at_most'] },
        amberBand: { type: 'number', minimum: 0 },
        amberBandType: { type: 'string', enum: ['absolute', 'percentage'] },
        onTargetWorseningRule: { type: 'boolean' },
        cadenceMinutes: { type: 'integer', minimum: 1 },
        sourceKind: { type: 'string', enum: ['task', 'person'] },
        sourceRef: { type: 'string' },
        isKey: { type: 'boolean' },
    },
    required: ['name'],
}

function setSchema(withTeam: boolean): JsonSchema {
    return {
        type: 'object',
        properties: {
            ...(withTeam
                ? {
                      teamId: {
                          type: 'string',
                          description: 'The team whose KPI targets are replaced.',
                      },
                  }
                : {}),
            kpis: {
                type: 'array',
                description: 'The complete KPI target list. Every item is validated by the API.',
                items: TARGET_ITEM,
            },
        },
        required: withTeam ? ['teamId', 'kpis'] : ['kpis'],
        additionalProperties: false,
    }
}

/** Company and team KPI target editors, over the normalized KPI table used by the board and readings. */
export const KPI_TARGET_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.get_enterprise_kpis',
        family: 'os.kpi',
        summary: "The company's KPI targets, bands, cadence and source.",
        description:
            "The company's KPI targets as set: unit, target and direction, AMBER band, cadence and source. A KPI waiting for a target has targetValue null. Each KPI carries signedOffAt: null while it waits for a person to sign it off, and until then no run sees it or records a reading for it.",
        inputSchema: {
            type: 'object',
            properties: {},
            additionalProperties: false,
        },
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: {
            method: 'GET',
            path: '/v1/enterprise/kpi-targets',
            scope: 'enterprise:read',
        },
        public: true,
    },
    {
        name: 'os.set_enterprise_kpis',
        family: 'os.kpi',
        summary: "Replace the company's KPI target list.",
        description:
            "Replace the company's complete KPI target list. A target needs its unit, direction, cadence and source; a no-target KPI is accepted. Uses the same validation as the portal. The answer's awaitingSignOff names every KPI still waiting for sign-off, or is null: an API token never signs a KPI off, so a KPI it adds or changes waits for a person.",
        inputSchema: setSchema(false),
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [
            {
                code: 'invalid_kpis',
                when: 'A KPI is incomplete or malformed.',
                handling: 'tool_error',
            },
        ],
        api: {
            method: 'PATCH',
            path: '/v1/enterprise/kpi-targets',
            scope: 'enterprise:write',
        },
        public: true,
    },
    {
        name: 'os.get_team_kpis',
        family: 'os.kpi',
        summary: "A team's KPI targets, including its key KPI.",
        description:
            "A team's own KPI targets as set, including which one is its key KPI. This is the target list, not the role and task KPI tree. Each KPI carries signedOffAt: null while it waits for a person to sign it off, and until then no run sees it or records a reading for it.",
        inputSchema: {
            type: 'object',
            properties: {
                teamId: {
                    type: 'string',
                    description: 'The team whose KPI targets are read.',
                },
            },
            required: ['teamId'],
            additionalProperties: false,
        },
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [
            {
                code: 'team_not_found',
                when: 'The team is not in this enterprise.',
                handling: 'tool_error',
            },
        ],
        api: {
            method: 'GET',
            path: '/v1/teams/:teamId/kpi-targets',
            scope: 'teams:read',
        },
        public: true,
    },
    {
        name: 'os.set_team_kpis',
        family: 'os.kpi',
        summary: "Replace a team's KPI target list and key KPI.",
        description:
            "Replace a team's complete KPI target list. Two key KPIs are refused; a list with none marked is stored with no key KPI. Uses the same validation as the portal. The answer's awaitingSignOff names every KPI still waiting for sign-off, or is null: an API token never signs a KPI off, so a KPI it adds or changes waits for a person.",
        inputSchema: setSchema(true),
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [
            {
                code: 'team_not_found',
                when: 'The team is not in this enterprise.',
                handling: 'tool_error',
            },
            {
                code: 'invalid_kpis',
                when: 'A KPI is incomplete or two KPIs are marked key.',
                handling: 'tool_error',
            },
        ],
        api: {
            method: 'PATCH',
            path: '/v1/teams/:teamId/kpi-targets',
            scope: 'teams:write',
        },
        public: true,
    },
]
